/**
 * exportAuditPdf — generates a clean, professional audit report PDF
 * using jsPDF directly (no screenshot — fully vector/text, always crisp).
 */
import jsPDF from 'jspdf'
import type { ProjectProfile } from './types'
import { CHAIN_LABELS, AUDIT_CHECKLIST, CHECK_KEYWORDS } from './types'

// ─── palette (matches site colours in greyscale-friendly way) ────────────────
const BLACK  = '#000000'
const WHITE  = '#ffffff'
const TURQ   = '#1ad6c4'
const MUTED  = '#8899aa'
const BORDER = '#222c35'
// Body text on the white page background (kept dark for readability)
const BODY   = '#111111'

// ─── severity colours ─────────────────────────────────────────────────────────
const SEV_COLOR: Record<string, string> = {
  critical: '#f87171',
  high:     '#fb923c',
  medium:   '#facc15',
  low:      '#60a5fa',
  info:     '#94a3b8',
}

// ─── brand logo (top-right of PDF header) ────────────────────────────────────
const LOGO_URL = 'https://static.lumi.new/9c/9c1ccefd81b9dbb855ed621cfae893f3.webp'

// load a remote image and return { dataUrl, width, height }, or null on failure
async function loadLogo(
  url: string,
): Promise<{ dataUrl: string; width: number; height: number } | null> {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    const dims = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = reject
      img.src = dataUrl
    })
    return { dataUrl, ...dims }
  } catch {
    return null
  }
}

// ─── helper: hex → r,g,b tuple ───────────────────────────────────────────────
function hex(h: string): [number, number, number] {
  const c = h.replace('#', '')
  return [
    parseInt(c.slice(0, 2), 16),
    parseInt(c.slice(2, 4), 16),
    parseInt(c.slice(4, 6), 16),
  ]
}

// ─── helper: draw a horizontal rule ──────────────────────────────────────────
function rule(doc: jsPDF, y: number, margin: number, pageW: number, color = BORDER) {
  doc.setDrawColor(...hex(color))
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
}

// ─── helper: section heading ──────────────────────────────────────────────────
function sectionHeading(doc: jsPDF, text: string, y: number, margin: number) {
  doc.setFontSize(7.5)
  doc.setTextColor(...hex(TURQ))
  doc.setFont('helvetica', 'bold')
  doc.text(text.toUpperCase(), margin, y)
  return y + 6
}

// ─── helper: wrapped body text ────────────────────────────────────────────────
function bodyText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  color = MUTED,
  size = 8.5,
): number {
  doc.setFontSize(size)
  doc.setTextColor(...hex(color === MUTED ? BODY : color))
  doc.setFont('helvetica', 'normal')
  const lines = doc.splitTextToSize(text, maxWidth)
  doc.text(lines, x, y)
  return y + lines.length * (size * 0.42)
}

// ─── derive checklist from audit ─────────────────────────────────────────────
function deriveChecklist(audit: NonNullable<ProjectProfile['audit']>) {
  const unresolvedText = audit.vulnerabilities
    .filter((v) => !v.resolved)
    .map((v) => `${v.title} ${v.description}`.toLowerCase())

  const overrides = audit.checklistOverrides ?? {}

  return AUDIT_CHECKLIST.map((item) => {
    const ov = overrides[item.id]
    // Manual override takes precedence when explicitly set to pass/fail
    if (ov && ov.state === 'pass') return { ...item, passed: true, reason: '' }
    if (ov && ov.state === 'fail') return { ...item, passed: false, reason: ov.reason ?? '' }

    // Fallback: derive from unresolved vulnerabilities
    const keywords = CHECK_KEYWORDS[item.id] ?? []
    const failed = unresolvedText.some((text) =>
      keywords.some((kw) => text.includes(kw)),
    )
    return { ...item, passed: !failed, reason: '' }
  })
}

// ─── main export function ─────────────────────────────────────────────────────
export async function exportAuditPdf(project: ProjectProfile): Promise<void> {
  const audit = project.audit
  if (!audit || audit.status !== 'completed') return

  const doc    = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageW  = doc.internal.pageSize.getWidth()
  const pageH  = doc.internal.pageSize.getHeight()
  const margin = 18
  const colW   = pageW - margin * 2
  let   y      = 0

  // ── helpers that auto-paginate ──────────────────────────────────────────────
  function checkPage(needed = 18) {
    if (y + needed > pageH - 18) {
      doc.addPage()
      // subtle header on continuation pages
      doc.setFillColor(...hex('#050a0d'))
      doc.rect(0, 0, pageW, 10, 'F')
      doc.setFontSize(7)
      doc.setTextColor(...hex(MUTED))
      doc.setFont('helvetica', 'normal')
      doc.text('Mandox Audits  ·  Confidential Audit Report', margin, 6.5)
      doc.text(`${project.name} (${project.symbol})`, pageW - margin, 6.5, { align: 'right' })
      y = 18
    }
  }

  // ══════════════════════════════════════════════════════════════════════════
  // COVER / HEADER
  // ══════════════════════════════════════════════════════════════════════════

  // Dark header band
  doc.setFillColor(...hex('#050a0d'))
  doc.rect(0, 0, pageW, 52, 'F')

  // Turquoise left accent
  doc.setFillColor(...hex(TURQ))
  doc.rect(0, 0, 3, 52, 'F')

  // Brand logo — top-right of header band
  const logo = await loadLogo(LOGO_URL)
  if (logo) {
    const logoH = 22 // mm
    const logoW = logo.height > 0 ? (logo.width / logo.height) * logoH : logoH
    const logoX = pageW - margin - logoW
    const logoY = (52 - logoH) / 2
    try {
      doc.addImage(logo.dataUrl, 'PNG', logoX, logoY, logoW, logoH, undefined, 'FAST')
    } catch {
      // ignore logo render failure — report still generates
    }
  }

  // Company label
  doc.setFontSize(8)
  doc.setTextColor(...hex(TURQ))
  doc.setFont('helvetica', 'bold')
  doc.text('MANDOX AUDITS', margin + 4, 14)

  // Tagline
  doc.setFontSize(6.5)
  doc.setTextColor(...hex(WHITE))
  doc.setFont('helvetica', 'normal')
  doc.text('Audit your token. Earn trust on-chain.', margin + 4, 19.5)

  // Site URL
  doc.setFontSize(7)
  doc.setTextColor(...hex(MUTED))
  doc.setFont('helvetica', 'normal')
  doc.text('Mandox.io', margin + 4, 24.5)

  // Project name
  doc.setFontSize(20)
  doc.setTextColor(...hex(WHITE))
  doc.setFont('helvetica', 'bold')
  doc.text(`${project.name}`, margin + 4, 35)

  // Symbol + chain pill
  doc.setFontSize(8.5)
  doc.setTextColor(...hex(TURQ))
  doc.setFont('helvetica', 'bold')
  doc.text(`$${project.symbol}  ·  ${CHAIN_LABELS[project.chain]}`, margin + 4, 44)

  // Audit date — center aligned
  if (audit.auditedAt) {
    const dateStr = new Date(audit.auditedAt).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    })
    doc.setFontSize(7.5)
    doc.setTextColor(...hex(MUTED))
    doc.setFont('helvetica', 'normal')
    doc.text(`Audited: ${dateStr}`, pageW / 2, 44, { align: 'center' })
  }

  y = 62

  // ── Verdict row ─────────────────────────────────────────────────────────────
  const passed      = audit.verdict === 'passed'
  const verdictColor = passed ? '#34d399' : '#f87171'
  const verdictLabel = passed ? 'AUDIT PASSED' : 'AUDIT FAILED'

  doc.setFontSize(16)
  doc.setTextColor(...hex(verdictColor))
  doc.setFont('helvetica', 'bold')
  doc.text(verdictLabel, margin, y)

  // Score badge (right side)
  if (audit.score != null) {
    const scoreX = pageW - margin - 22
    const scoreY = y - 10

    // circle background
    doc.setFillColor(...hex('#0d1a18'))
    doc.circle(scoreX, scoreY, 12, 'F')
    doc.setDrawColor(...hex(verdictColor))
    doc.setLineWidth(1.2)
    doc.circle(scoreX, scoreY, 12, 'S')

    doc.setFontSize(13)
    doc.setTextColor(...hex(WHITE))
    doc.setFont('helvetica', 'bold')
    doc.text(String(audit.score), scoreX, scoreY + 1.5, { align: 'center' })

    doc.setFontSize(5.5)
    doc.setTextColor(...hex(MUTED))
    doc.setFont('helvetica', 'normal')
    doc.text('/100', scoreX, scoreY + 6.5, { align: 'center' })
  }

  y += 6

  // Contract address
  doc.setFontSize(7.5)
  doc.setTextColor(...hex(BODY))
  doc.setFont('helvetica', 'normal')
  doc.text(`Contract: ${project.contractAddress}`, margin, y)
  y += 5

  rule(doc, y, margin, pageW, TURQ)
  y += 8

  // ── Audit Summary ───────────────────────────────────────────────────────────
  if (audit.summary) {
    y = sectionHeading(doc, 'Audit Summary', y, margin)
    y = bodyText(doc, audit.summary, margin, y, colW, MUTED, 9)
    y += 8
  }

  // ── About / Bio ─────────────────────────────────────────────────────────────
  if (project.bio || project.description) {
    checkPage()
    y = sectionHeading(doc, 'About This Project', y, margin)
    y = bodyText(doc, project.bio || project.description, margin, y, colW, MUTED, 9)
    y += 8
  }

  // ══════════════════════════════════════════════════════════════════════════
  // SECURITY CHECKLIST
  // ══════════════════════════════════════════════════════════════════════════
  checkPage(20)
  const checklist    = deriveChecklist(audit)
  const passedChecks = checklist.filter((i) => i.passed).length

  y = sectionHeading(doc, `Security Checklist  —  ${passedChecks}/${checklist.length} Passed`, y, margin)

  // 2-column layout
  const colHalf = (colW - 8) / 2

  checklist.forEach((item, idx) => {
    checkPage(16)
    const col = idx % 2
    const x   = margin + col * (colHalf + 8)
    const rowH = 14

    if (col === 0 && idx > 0) y += 0 // rows advance below

    const checkColor = item.passed ? '#34d399' : '#f87171'
    const statusLabel = item.passed ? 'PASS' : 'FAIL'

    // Row bg
    doc.setFillColor(...hex(item.passed ? '#0a1a14' : '#1a0a0e'))
    doc.roundedRect(x, y - 5, colHalf, rowH, 2, 2, 'F')

    // Check mark / X (ASCII-safe so the default font always renders it)
    doc.setFontSize(9)
    doc.setTextColor(...hex(checkColor))
    doc.setFont('helvetica', 'bold')
    doc.text(item.passed ? 'P' : 'X', x + 3.5, y + 1.5)

    // Label
    doc.setFontSize(8)
    doc.setTextColor(...hex(WHITE))
    doc.setFont('helvetica', 'bold')
    const labelLines = doc.splitTextToSize(item.label, colHalf - 22)
    doc.text(labelLines[0], x + 9, y + 1.5)

    // Status badge
    doc.setFontSize(6.5)
    doc.setTextColor(...hex(checkColor))
    doc.setFont('helvetica', 'bold')
    doc.text(statusLabel, x + colHalf - 4, y + 1.5, { align: 'right' })

    // Description
    const descLines = doc.splitTextToSize(item.description, colHalf - 12)
    doc.setFontSize(6.5)
    doc.setTextColor(...hex(BODY))
    doc.setFont('helvetica', 'normal')
    doc.text(descLines[0], x + 9, y + 6.5)

    // Advance y after every second item (end of row)
    if (col === 1 || idx === checklist.length - 1) {
      y += rowH + 3
    }
  })

  y += 6

  // ══════════════════════════════════════════════════════════════════════════
  // VULNERABILITY REPORT
  // ══════════════════════════════════════════════════════════════════════════
  const vulns = audit.vulnerabilities
  if (vulns.length > 0) {
    checkPage(20)
    const crit      = vulns.filter((v) => v.severity === 'critical' && !v.resolved).length
    const high      = vulns.filter((v) => v.severity === 'high'     && !v.resolved).length
    const resolved  = vulns.filter((v) => v.resolved).length
    const summary   = [
      crit     > 0 ? `${crit} Critical`      : '',
      high     > 0 ? `${high} High`           : '',
      resolved > 0 ? `${resolved} Resolved`   : '',
    ].filter(Boolean).join('  ·  ')

    y = sectionHeading(
      doc,
      `Vulnerability Report${summary ? `  —  ${summary}` : ''}`,
      y,
      margin,
    )

    const order: Array<'critical' | 'high' | 'medium' | 'low' | 'info'> = [
      'critical', 'high', 'medium', 'low', 'info',
    ]

    order.forEach((sev) => {
      const group = vulns.filter((v) => v.severity === sev)
      if (!group.length) return

      group.forEach((v) => {
        checkPage(24)
        const barColor = v.resolved ? '#34d399' : (SEV_COLOR[sev] ?? MUTED)

        // Left accent bar
        doc.setFillColor(...hex(barColor))
        doc.rect(margin, y - 4, 2.5, 18, 'F')

        // Title
        doc.setFontSize(9)
        doc.setTextColor(...hex(WHITE))
        doc.setFont('helvetica', 'bold')
        const titleLines = doc.splitTextToSize(v.title, colW - 40)
        doc.text(titleLines, margin + 6, y)

        // Severity pill
        doc.setFontSize(7)
        doc.setTextColor(...hex(barColor))
        doc.setFont('helvetica', 'bold')
        const sevLabel = sev.charAt(0).toUpperCase() + sev.slice(1)
        const pillTxt  = v.resolved ? `${sevLabel}  ·  Resolved` : sevLabel
        doc.text(pillTxt, pageW - margin, y, { align: 'right' })

        y += titleLines.length * 4 + 1

        // Description
        const descLines = doc.splitTextToSize(v.description, colW - 8)
        doc.setFontSize(8)
        doc.setTextColor(...hex(BODY))
        doc.setFont('helvetica', 'normal')
        doc.text(descLines, margin + 6, y)
        y += descLines.length * 3.8 + 10

        rule(doc, y - 5, margin, pageW)
      })
    })
  } else {
    // Zero vulnerabilities
    checkPage(20)
    y = sectionHeading(doc, 'Vulnerability Report', y, margin)
    doc.setFontSize(9)
    doc.setTextColor(...hex('#34d399'))
    doc.setFont('helvetica', 'bold')
    doc.text('Zero vulnerabilities found - contract passed all security checks.', margin, y)
    y += 10
  }

  // ══════════════════════════════════════════════════════════════════════════
  // FOOTER — last page
  // ══════════════════════════════════════════════════════════════════════════
  checkPage(30)
  y += 6
  rule(doc, y, margin, pageW, TURQ)
  y += 7

  doc.setFontSize(7.5)
  doc.setTextColor(...hex(MUTED))
  doc.setFont('helvetica', 'normal')
  const disclaimer =
    'This report was produced by Mandox Audits and reflects the state of the smart contract ' +
    'at the time of audit. It does not constitute financial or investment advice. No warranty is given ' +
    'as to the absence of undiscovered vulnerabilities. Always exercise due diligence before interacting ' +
    'with any smart contract.'
  doc.setTextColor(...hex(BODY))
  const dLines = doc.splitTextToSize(disclaimer, colW)
  doc.text(dLines, margin, y)
  y += dLines.length * 3.8 + 6

  doc.setFontSize(7)
  doc.setTextColor(...hex(BLACK))
  doc.setFont('helvetica', 'bold')
  doc.text('Mandox.io', margin, y)

  doc.setTextColor(...hex(MUTED))
  doc.setFont('helvetica', 'normal')
  doc.text(
    `Report generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`,
    pageW - margin,
    y,
    { align: 'right' },
  )

  // Page numbers on every page
  const totalPages = (doc.internal as unknown as { getNumberOfPages(): number }).getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFontSize(6.5)
    doc.setTextColor(...hex(MUTED))
    doc.setFont('helvetica', 'normal')
    doc.text(`Page ${i} of ${totalPages}`, pageW / 2, pageH - 8, { align: 'center' })
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  const filename = `${project.name.replace(/\s+/g, '_')}_${project.symbol}_AuditReport.pdf`
  doc.save(filename)
}
